import React from 'react';
import styled, { keyframes } from 'styled-components';
import { NAVBAR_HEIGHT } from '../../../components/Navbar';
import DashboardPage from '../../../components/DashboardPage';
import useAuth from '../../../hooks/use-auth';
import { ROUTES } from '../../../routing';
import { CmsModuleDefinitionSchema } from '../../../schemas/content-module';
import { cmsModuleToDashboardResponse } from '../../../lib/cms-module-to-dashboard';
import type { DashboardPayload } from '../../../hydra';
import { useParams } from 'react-router-dom';

const PREVIEW_STORAGE_KEY = 'enki:preview:module';
const PREVIEW_MAX_AGE_MS = 60 * 60 * 1000;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const FadeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  animation: ${fadeIn} 0.4s ease-in-out forwards;
  min-height: 100vh;
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - ${NAVBAR_HEIGHT}px);
  gap: 16px;
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 18px;
`;

export default function CMSPreviewModule() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const [dashboardData, setDashboardData] =
    React.useState<DashboardPayload | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(PREVIEW_STORAGE_KEY);
      if (!raw) {
        setError(
          'No preview data found. Open the preview from the editor page.'
        );
        return;
      }

      const parsed = JSON.parse(raw);

      if (
        parsed._createdAt &&
        Date.now() - parsed._createdAt > PREVIEW_MAX_AGE_MS
      ) {
        localStorage.removeItem(PREVIEW_STORAGE_KEY);
        setError(
          'Preview data has expired. Open a new preview from the editor.'
        );
        return;
      }

      const result = CmsModuleDefinitionSchema.safeParse(parsed);
      if (!result.success) {
        setError(
          `Invalid preview data: ${result.error.issues
            .map((i) => i.message)
            .join(', ')}`
        );
        return;
      }

      setDashboardData(cmsModuleToDashboardResponse(result.data));
    } catch (e) {
      setError(`Failed to load preview data: ${(e as Error).message}`);
    }
  }, []);

  const getActivityUrl = (
    _mission: { slug: string },
    activity: { slug: string }
  ) =>
    ROUTES.ADMIN.CMS_MODULE_PREVIEW_ACTIVITY.getPath({
      slug: slug ?? '',
      activitySlug: activity.slug,
    });

  if (error) {
    return (
      <FadeWrapper>
        <ErrorWrapper>{error}</ErrorWrapper>
      </FadeWrapper>
    );
  }

  const statsState = {
    value: dashboardData,
    loading: false,
    error: null,
    retry: () => {},
  };

  return (
    <FadeWrapper>
      <DashboardPage
        user={user}
        statsState={statsState}
        getActivityUrl={getActivityUrl}
        isPreview
      />
    </FadeWrapper>
  );
}
